import React, { useState, useRef, useEffect } from "react";
import { parseDate, getLocalTimeZone } from "@internationalized/date";
import DatePickerField from "../../../common/DatePickerDashboard";
import NoDataFound from "../../../common/NoDataFound";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFilteredData,
  getDashboardPolicyListAction,
} from "../../../redux/actions/dashboardAction";
import PolicyDetails from "./PolicyDetails";
import Loader from "../../loader/loader";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { selectDashboardPolicyDataList } from "../../../redux/selectors/dashboardSelectors";
import { useTheme, useMediaQuery } from "@mui/material";

import ReusableTable from "../../../common/ReusableTable";

const DashboardPolicyData = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
  const [toDate, setToDate] = useState(dayjs());
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isRowClicked, setIsRowClicked] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filteredData, setFilteredData] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const navigate = useNavigate();
  const didRunRef = useRef(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const getToday = () => {
    const today = new Date();
    return parseDate(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(today.getDate()).padStart(2, "0")}`
    );
  };

  const getFirstOfMonth = () => {
    const today = new Date();
    return parseDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`);
  };

  const formatForForm = (date) => {
    const d = date.toDate(getLocalTimeZone());
    return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(
      2,
      "0"
    )}-${d.getFullYear()}`;
  };

  const initialFormData = {
    fromDate: formatForForm(getFirstOfMonth()),
    toDate: formatForForm(getToday()),
  };

  const [formData, setFormData] = useState(initialFormData);

  const handleDateChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleBlur = (fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
  };

  const dashboardPolicyDataList = useSelector(selectDashboardPolicyDataList);
  const { loading, error } = useSelector((state) => state.dashboardData);
  const paginationData =
    useSelector((state) => state.dashboardData.policies?.policiesPage) || {};

  useEffect(() => {
    if (isFiltered) return;

    if (process.env.NODE_ENV === "development") {
      if (didRunRef.current) return;
      didRunRef.current = true;
    }

    dispatch(getDashboardPolicyListAction(page, rowsPerPage));
  }, [page, rowsPerPage, isFiltered, dispatch]);

  useEffect(() => {
    setFilteredData(dashboardPolicyDataList);
  }, [dashboardPolicyDataList]);

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  const handleApply = () => {
    const payload = {
      fromDate: dayjs(formData.fromDate).format("MM-DD-YYYY") || "",
      toDate: dayjs(formData.toDate).format("MM-DD-YYYY") || "",
      lineOfBusiness: selectedFilter,
    };

    if ((fromDate && toDate) || selectedFilter !== "All") {
      dispatch(fetchFilteredData(payload, page, rowsPerPage));
      setIsFiltered(true);
    } else {
      setFilteredData(dashboardPolicyDataList);
      setIsFiltered(false);
    }

    
    const filtered = dashboardPolicyDataList.filter((policy) => {
      const isWithinDateRange =
        dayjs(policy.effectiveDate).isBetween(fromDate, toDate, null, "[]") ||
        dayjs(policy.expirationDate).isBetween(fromDate, toDate, null, "[]");
      const matchesFilter = selectedFilter === "All" || policy.lineOfBusiness === selectedFilter;
      return isWithinDateRange && matchesFilter;
    });
    setFilteredData(filtered);
  };

  const handleRowClick = (rowData) => {
    const formattedData = {
      id: rowData.id,
      firstName: rowData.name?.split(" ")[0] || "",
      middleName: null,
      lastName: rowData.name?.split(" ")[1] || "",
      email: rowData.email || null,
      policyNumber: rowData.policyNumber,
      policyType: rowData.policyType,
      dateOfBirth: null,
      phoneNumber: rowData.phoneNumber,
      company: rowData.company,
      licenseNumber: null,
      effectiveDate: rowData.effectiveDate,
      expiryDate: rowData.expiryDate,
      status: rowData.status,
      lineOfBusiness: rowData.lineOfBusiness,
      premium: rowData.premium,
    };
    navigate("/globalsearch", {
      state: {
        id: formattedData.id,
        showPolicyDetails: true,
        clientCustomerId: rowData.clientCustomerId,
        selectedPolicyData: rowData,
      },
    });
    dispatch({ type: "CLEAR_GET_CLIENT_SUCCESS" });
    dispatch({ type: "CLEAR_GET_CUSTOMER_DETAILS_SUCCESS" });
    dispatch({ type: "CLEAR_CUSTOMER_SEARCH_LIST_SUCCESS" });
  };

  const handleReset = () => {
    const defaultFromDate = dayjs().startOf("month");
    const defaultToDate = dayjs();
    setFormData({
      fromDate: formatForForm(defaultFromDate),
      toDate: formatForForm(defaultToDate),
    });
    setToDate(defaultToDate);
    setSelectedFilter("All");
    setIsFiltered(false);
    setPage(0);
    setFieldErrors({});
    setTouchedFields({});
    dispatch(getDashboardPolicyListAction(0, rowsPerPage));
  };

  const handleClose = () => {
    setOpen(false);
  };

  const formatPhoneNumber = (number) => {
    if (!number) return "";
    const cleaned = ("" + number).replace(/\D/g, "");
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return cleaned.slice(1).replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
    }
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
    }
    return number;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const payload = {
      fromDate: dayjs(formData.fromDate).format("MM-DD-YYYY") || "",
      toDate: dayjs(formData.toDate).format("MM-DD-YYYY") || "",
      lineOfBusiness: selectedFilter,
    };
    if (isFiltered) {
      dispatch(fetchFilteredData(payload, newPage, rowsPerPage));
    } else {
      dispatch(getDashboardPolicyListAction(newPage, rowsPerPage));
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    const payload = {
      fromDate: dayjs(formData.fromDate).format("MM-DD-YYYY") || "",
      toDate: dayjs(formData.toDate).format("MM-DD-YYYY") || "",
      lineOfBusiness: selectedFilter,
    };

    if (isFiltered) {
      dispatch(fetchFilteredData(payload, 0, newRowsPerPage));
    } else {
      dispatch(getDashboardPolicyListAction(0, newRowsPerPage));
    }
  };

 
  const columns = [
    {
      id: "name",
      label: "Name",
      render: (row) =>
        [row?.firstName, row?.middleName, row?.lastName].filter(Boolean).join(" "),
    },
    { id: "policyNumber", label: "Policy Number", field: "policyNumber" },
    { id: "phone", label: "Phone Number", render: (row) => formatPhoneNumber(row?.phoneNumber) },
    { id: "policyType", label: "Policy Type", field: "policyType" },
    { id: "company", label: "Company", field: "company" },
    { id: "lob", label: "Line of Business", field: "lineOfBusiness" },
    { id: "eff", label: "Eff Date", field: "effectiveDate" },
    { id: "exp", label: "Exp Date", field: "expirationDate" },
    { id: "status", label: "Status", render: (row) => row?.status || "active" },
    { id: "premium", label: "Premium", field: "premium", align: "right" },
  ];

  return (
    <Box>
      <Box
        sx={{
          padding: isMobile ? "8px" : "10px",
          backgroundColor: "#ffffff",
          overflowX: "auto",
          minHeight: "40vh",
          "@media (min-width: 768px) and (max-width: 1024px)": {
            minHeight: "100vh",
            height: "auto",
            overflowY: "auto",
          },
          "@media (min-width: 1024px) and (min-height: 1366px)": {
            height: "auto",
            minHeight: "70vh",
          },
        }}
      >
         <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            alignItems: "flex-start",
            marginBottom: "12px",
            flexWrap: "wrap",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              width: isMobile ? "100%" : "215px",
              minWidth: isMobile ? "100%" : "215px",
              minHeight: "85px",
            }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
              From Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePickerField
                fieldName="fromDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
                size={isMobile ? "small" : "medium"}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": { height: "55px" },
                }}
                inputProps={{ id: "fromDate", name: "fromDate" }}
              />
            </LocalizationProvider>
          </Box>

          <Box
            sx={{
              width: isMobile ? "100%" : "215px",
              minWidth: isMobile ? "100%" : "215px",
              minHeight: "85px",
            }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
              To Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePickerField
                fieldName="toDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
                size={isMobile ? "small" : "medium"}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": { height: "55px" },
                }}
                inputProps={{ id: "toDate", name: "toDate" }}
              />
            </LocalizationProvider>
          </Box>

            <FormControl
              sx={{
                width: isMobile ? "100%" : "200px",
                minWidth: isMobile ? "100%" : "200px",
                minHeight: "85px",
              }}
            >
              <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
                Line of Business
              </Typography>
              <Select
                native
                value={selectedFilter}
                onChange={handleFilterChange}
                size={isMobile ? "small" : "medium"}
                sx={{
                  width: "100%",
                  height: "55px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    height: "100% !important",
                  },
                }}
                inputProps={{
                  id: "line-of-business-select",
                  "aria-label": "Line of Business",
                }}
              >
                <option value="All">All</option>
                <option value="Personal Auto">Personal Auto</option>
                <option value="Commercial Auto">Commercial Auto</option>
                <option value="Homeowners">Homeowners</option>
                <option value="Health">Health</option>
                <option value="General Liability">General Liability</option>
              </Select>
            </FormControl>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                width: isMobile ? "100%" : "auto",
                minHeight: "85px",
                alignItems: "flex-end",
                marginTop: "-3px",
                "& button": {
                  flex: isMobile ? 1 : "none",
                  height: "55px",
                  minHeight: "55px",
                },
              }}
            >
              <Button
                variant="contained"
                id="apply-filter-button"
                onClick={handleApply}
                size={isMobile ? "small" : "medium"}
                disabled={!fromDate || !toDate}
              >
                Apply
              </Button>
              <Button
                onClick={handleReset}
                size={isMobile ? "small" : "medium"}
                variant="outlined"
                id="reset-button"
              >
                Reset
              </Button>
            </Box>
          </Box>
        </Box>
        {!isFiltered ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
          <NoDataFound
            discription="Please use the search filters above to fetch and display updated Policies data."
            message="After click Apply- results will shown as per selected filter"
          />
          </Box>
        ) : filteredData.length === 0 ? (
           <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
                backgroundColor: '#f9f9f9',
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}>
            <NoDataFound
              title="No Data Found"
              discription="Please change search filters range where data is available as per defined range"
              message="After click Apply- results will shown as per selected filter"
            />
          </Box>
        ) : (
          <ReusableTable
            columns={columns}
            rows={filteredData}
            loading={loading}
            error={error}
            LoaderComponent={Loader}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
            totalPages={paginationData?.totalPages || 0}
            totalElements={paginationData?.totalElements || 0}
            onPageChange={(next) => handleChangePage(null, next)}
            onRowsPerPageChange={handleChangeRowsPerPage}
            getRowId={(row) => row.id}
            onRowClick={(row) => handleRowClick(row)}
            size={isMobile ? "small" : "medium"}
            tableSx={{
              minWidth: isMobile ? "100%" : "650px",
            }}
            containerSx={{
              maxHeight: isMobile ? "60vh" : "none",
              overflow: "auto",
              width: "100%",
            }}
          />
        )}
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle id="policydetailsdialogtitle">Policy Details</DialogTitle>
        <DialogContent>
          <PolicyDetails policyId={selectedId} isRowClicked={isRowClicked} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} id="close-button">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPolicyData;
